import { type Money, ZERO, sum, sub, money } from "../money/money";
import { priceCart, priceLine, type CartLineInput, type TaxConfig } from "../pricing/price-cart";
import { computeStatutoryDiscount } from "../pricing/statutory";
import { formatDocumentNumber } from "../numbering/series";
import { businessDate, type BusinessDate, type IsoTimestamp } from "../time";

/**
 * Sale finalization — the moment a cart becomes a permanent financial record.
 *
 * After this, nothing about the sale changes. Corrections happen by ADDING void
 * and refund records that reference it (Principle II). There is no code path in
 * this repository that updates a finalized sale, and the boundary checker fails
 * the build if one appears.
 *
 * The heaviest-carrying property here is the SNAPSHOT. A finalized sale stores
 * everything a receipt needs — item names, prices, modifier names, the tax
 * regime and rate in force — so that a reprint six months later reproduces the
 * original exactly, after the item has been renamed, repriced, retired, and the
 * business has changed tax regime (FR-005, FR-018, FR-024, FR-027c).
 */

export class JournalError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "JournalError";
  }
}

export interface TenderInput {
  id: string;
  type: string;
  amount: Money;
  reference: string | null;
}

export interface DiscountInput {
  id: string;
  saleLineId: string | null;
  type: "promotional" | "senior_citizen" | "pwd";
  basis: string | null;
  rateBp: number;
  amount: Money;
  coveredCustomers: number | null;
  totalCustomers: number | null;
  cardholderName: string | null;
  cardholderId: string | null;
  authorizedBy: string | null;
  /**
   * The module that granted this discount, when one did (a line grant —
   * specs/006-loyalty research R3). Absent for every core discount.
   */
  grantedByModule?: string;
}

export interface FinalizeInput {
  saleId: string;
  series: string;
  sequence: number;
  terminalId: string;
  shiftId: string;
  staffId: string;
  orderType: string;
  customerName: string | null;
  lines: CartLineInput[];
  tenders: TenderInput[];
  discounts: DiscountInput[];
  tax: TaxConfig;
  activeModules: string[];
  timezone: string;
  now: Date;
}

export interface FinalizedLineModifier {
  id: string;
  modifierId: string;
  nameSnapshot: string;
  priceAdjustment: Money;
}

export interface FinalizedLine {
  id: string;
  saleId: string;
  lineNumber: number;
  menuItemId: string;
  variantId: string | null;
  itemNameSnapshot: string;
  variantNameSnapshot: string | null;
  quantity: number;
  unitPrice: Money;
  modifierTotal: Money;
  lineSubtotal: Money;
  lineDiscount: Money;
  lineTotal: Money;
  statutoryEligible: boolean;
  note: string | null;
  modifiers: readonly FinalizedLineModifier[];
}

export interface FinalizedTender {
  id: string;
  saleId: string;
  type: string;
  amount: Money;
  reference: string | null;
  changeGiven: Money;
}

export interface FinalizedSale {
  id: string;
  documentNumber: string;
  series: string;
  sequence: number;
  terminalId: string;
  shiftId: string;
  staffId: string;
  orderType: string;
  customerName: string | null;
  status: "finalized";

  subtotal: Money;
  discountTotal: Money;
  statutoryDiscountTotal: Money;
  taxableAmount: Money;
  taxExemptAmount: Money;
  zeroRatedAmount: Money;
  taxAmount: Money;
  total: Money;
  changeGiven: Money;

  taxRegime: string;
  taxRateBp: number;

  activeModules: readonly string[];
  businessDate: BusinessDate;
  createdAt: IsoTimestamp;

  lines: readonly FinalizedLine[];
  tenders: readonly FinalizedTender[];
  discounts: readonly DiscountInput[];
}

/** Tender types that can be over-paid, producing change. */
const CASH_TENDERS = new Set(["cash"]);

export function isCashTender(type: string): boolean {
  return CASH_TENDERS.has(type);
}

export function finalizeSale(input: FinalizeInput): FinalizedSale {
  if (input.lines.length === 0) {
    throw new JournalError("Cannot finalize a sale with no lines");
  }
  // "No tender" is decided after pricing, below: a sale whose total is ₱0 (a
  // free item granted by a module, a 100% discount) has nothing to tender.

  // A cart may transiently hold a zero quantity while someone is editing it.
  // A FINALIZED line may not — it would be a permanent record of selling
  // nothing, and it would appear on the receipt.
  for (const line of input.lines) {
    if (!Number.isInteger(line.quantity) || line.quantity < 1) {
      throw new JournalError(
        `Line ${line.lineId}: quantity must be at least 1 to finalize, got ${line.quantity}`,
      );
    }
  }

  // A statutory discount record drives the SPECIAL branch of priceCart (VAT
  // stripped from the eligible amount, 20% off that base) — re-derived here
  // from input.lines and the record's own coveredCustomers/totalCustomers/
  // rateBp rather than trusting a client-supplied total, the same reason the
  // Worker re-validates every other figure on sync (Principle VII). A plain
  // sale-level discount (saleLineId === null, type "promotional" — which
  // does not exist in the live UI today, since DiscountDialog spreads its
  // reduction onto lines instead, but the field stays honoured here for any
  // future sale-level promo path) takes the ordinary branch. order-
  // discounts.ts's rule means a sale never carries both.
  const statutoryRecord = input.discounts.find(
    (d) => d.type === "senior_citizen" || d.type === "pwd",
  );

  const priced = statutoryRecord
    ? priceCart(input.lines, input.tax, {
        statutoryDiscount: computeStatutoryDiscount({
          eligibleAmount: sum(
            input.lines
              .map(priceLine)
              .filter((line) => line.statutoryEligible)
              .map((line) => line.lineTotal),
          ),
          coveredCustomers: statutoryRecord.coveredCustomers ?? 1,
          totalCustomers: statutoryRecord.totalCustomers ?? 1,
          tax: input.tax,
          statutoryRateBp: statutoryRecord.rateBp,
        }),
      })
    : priceCart(input.lines, input.tax, {
        saleDiscount: sum(
          input.discounts.filter((d) => d.saleLineId === null).map((d) => d.amount),
        ),
      });

  // Something due and nothing tendered is a sale with no payment. Nothing due
  // and nothing tendered is a sale paid for in full by its discounts, and has
  // no cash in it — so the drawer, which opens only on cash, stays shut.
  if (input.tenders.length === 0 && priced.total > 0) {
    throw new JournalError("Cannot finalize a sale with no tender");
  }

  const tendered = sum(input.tenders.map((t) => t.amount));
  if (tendered < priced.total) {
    throw new JournalError(
      `Tendered ${tendered} is less than the amount due ${priced.total}`,
    );
  }

  const overpayment = sub(tendered, priced.total);

  // Change comes out of the drawer, so it can only arise from cash. An
  // over-tendered e-wallet or card is a data-entry error, not a reason to open
  // the drawer and hand money back.
  const cashTendered = sum(
    input.tenders.filter((t) => isCashTender(t.type)).map((t) => t.amount),
  );
  if (overpayment > 0 && overpayment > cashTendered) {
    throw new JournalError(
      "Overpayment exceeds the cash tendered; non-cash tenders cannot produce change",
    );
  }
  const changeGiven = overpayment > 0 ? money(overpayment) : ZERO;

  const lines: FinalizedLine[] = priced.lines.map((line, index) =>
    Object.freeze({
      id: line.lineId,
      saleId: input.saleId,
      lineNumber: index + 1,
      menuItemId: line.menuItemId,
      variantId: line.variantId,
      itemNameSnapshot: line.itemName,
      variantNameSnapshot: line.variantName,
      quantity: line.quantity,
      unitPrice: line.unitPrice,
      modifierTotal: line.modifierTotal,
      lineSubtotal: line.lineSubtotal,
      lineDiscount: line.lineDiscount,
      lineTotal: line.lineTotal,
      statutoryEligible: line.statutoryEligible,
      note: line.note,
      modifiers: Object.freeze(
        line.modifiers.map((modifier) =>
          Object.freeze({
            id: `${line.lineId}:${modifier.id}`,
            modifierId: modifier.id,
            nameSnapshot: modifier.name,
            priceAdjustment: modifier.priceAdjustment,
          }),
        ),
      ),
    }),
  );

  // Change is attributed to the last cash tender, which is where it physically
  // comes from at the drawer.
  const lastCashIndex = input.tenders.map((t) => isCashTender(t.type)).lastIndexOf(true);

  const tenders: FinalizedTender[] = input.tenders.map((tender, index) =>
    Object.freeze({
      id: tender.id,
      saleId: input.saleId,
      type: tender.type,
      amount: tender.amount,
      reference: tender.reference,
      changeGiven: index === lastCashIndex ? changeGiven : ZERO,
    }),
  );

  const sale: FinalizedSale = {
    id: input.saleId,
    documentNumber: formatDocumentNumber(input.series, input.sequence),
    series: input.series,
    sequence: input.sequence,
    terminalId: input.terminalId,
    shiftId: input.shiftId,
    staffId: input.staffId,
    orderType: input.orderType,
    customerName: input.customerName,
    status: "finalized",

    subtotal: priced.subtotal,
    discountTotal: priced.discountTotal,
    statutoryDiscountTotal: priced.statutoryDiscountTotal,
    taxableAmount: priced.taxableAmount,
    taxExemptAmount: priced.taxExemptAmount,
    zeroRatedAmount: priced.zeroRatedAmount,
    taxAmount: priced.taxAmount,
    total: priced.total,
    changeGiven,

    // In force AT TIME OF SALE. Never re-read from current configuration.
    taxRegime: priced.taxRegime,
    taxRateBp: priced.taxRateBp,

    activeModules: Object.freeze([...input.activeModules]),
    businessDate: businessDate(input.now, input.timezone),
    createdAt: input.now.toISOString(),

    lines: Object.freeze(lines),
    tenders: Object.freeze(tenders),
    discounts: Object.freeze([...input.discounts]),
  };

  // Frozen so the immutability guarantee is enforced by the runtime and not
  // merely promised by a comment.
  return Object.freeze(sale);
}
